import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Core
import { appConfig } from '../../config/app-config';
import { DataHubService } from '../../core/datahub/datahub.service';
import { ErrorMessage } from '../../core/model/error-message';
import { INavRequestOption } from '../model/nav-request-option.interface';

// navigation
import { HttpParamsOptions } from '@angular/common/http/src/params';
import { UiMessageSnackService } from '../../core/components/message-snack/service/message-snack.service';
import { navigationConfig } from '../config/navigation.config';
import { INavElementsEdge } from '../model/nav-elements-edgegroup';
import { INavNewEdgeAndElement } from '../model/nav-new-edge-element.interface';


@Injectable()
export class NavigationDatahubService extends DataHubService {

    constructor(
        http: HttpClient,
        log: UiMessageSnackService,
    ) {
        super(http, log);
    }

    // ---------------------------------------------------------------------------------------------
    // --- Config
    // ---------------------------------------------------------------------------------------------
    public loadNavigationConfig(): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:loadNavigationConfig(called)'); }

        // request params setup
        const __params = new HttpParams();
        let __url = navigationConfig.urlDataGetConfig;

        // mocking case
        if (appConfig.dev.mockingMode) {
            // log
            console.warn('Mocking HTTP request: ', __url);

            __url = `${appConfig.dev.mockingDataFolder}/nav-config.json`;
            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    // ---------------------------------------------------------------------------------------------
    // --- Elements
    // ---------------------------------------------------------------------------------------------
    public loadElementsInitial(
        requestOption: INavRequestOption
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:loadElementsInitial(called)'); }

        // edges: map array to single string
        let edgeParam = '';
        let index = 0;
        requestOption.edges.forEach(elem => {
            if (elem.search) {
                edgeParam = (index++ === 0) ? elem.value : edgeParam + ',' + elem.value;
            }
        });

        // request params setup
        const __params = new HttpParams()
            .set('input', encodeURI(requestOption.input))
            .set('maxnodes', requestOption.maxnodes.toString())
            .set('edges', edgeParam);

        let __url = navigationConfig.urlDataInitialSearch;

        // mocking case
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            // simulate error_respond
            if (requestOption.input && requestOption.input === '#!/error') {
                __url = `${appConfig.dev.mockingDataFolder}/nav-search-respond-error.json`;
                return this.readFileJsonSchema(
                    __url,
                    navigationConfig.debugMode
                );
            } else if (requestOption.input && requestOption.input === '#!/result') {
                __url = `${appConfig.dev.mockingDataFolder}/nav-result.json`;
                return this.readFileJsonSchema(
                    __url,
                    navigationConfig.debugMode
                );
            } else {
                __url = `${appConfig.dev.mockingDataFolder}/nav-search-respond-initial.json`;
                return this.readFileJsonSchema(
                    __url,
                    navigationConfig.debugMode
                );
            }
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    public findDocumentOfCollection(
        collection: string,
        opts: HttpParamsOptions
    ) {
        const __params = new HttpParams(opts);
        const __url = navigationConfig.urlDataGetDetails + '/' + collection;

        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    public postChecklist(
        ids: string[],
    ) {
        const __url = navigationConfig.urlDocumentChecklist;

        return this.httpPostAsBlob(
            __url,
            navigationConfig.debugMode,
            ids,
        );
    }

    public postManualChecklist(
        ids: string[],
    ) {
        const __url = navigationConfig.urlDocumentManualChecklist;

        return this.httpPostAsBlob(
            __url,
            navigationConfig.debugMode,
            ids,
        );
    }

    public loadElementsDetail(
        elementID: string
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:loadElementsDetail(called)'); }

        // request params setup
        const __params = new HttpParams()
            .set('uischema', 'true');
        let __url = navigationConfig.urlDataGetDetails + '/' + elementID;

        // mocking case
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            __url = `${appConfig.dev.mockingDataFolder}/nav_detail.json`;
            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    public loadSchema(
        elementType: string
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:loadSchema(called)'); }

        // request params setup
        const __params = new HttpParams()
            .set('id', elementType);
        let __url = navigationConfig.urlDataCollectionSchema;

        // mocking case
        if (appConfig.dev.mockingMode) {

            __url = `${appConfig.dev.mockingDataFolder}/nav-schema.json`;
            console.warn('Mocking HTTP request: ', __url);

            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    public loadUiSchema(
        elementType: string
    ): Promise<any> {

        // request params setup
        const __params = new HttpParams()
            .set('collection', elementType);
        let __url = navigationConfig.urlDataCollectionUiSchema;

        // mocking case
        if (appConfig.dev.mockingMode) {

            __url = `${appConfig.dev.mockingDataFolder}/nav-uischema.json`;
            console.warn('Mocking HTTP request: ', __url);

            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    public saveElementsDetail(
        elementDetails: any,
        confirmed?: boolean,
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:saveElementsDetail(called)'); }
        // sanity
        if (!elementDetails || !elementDetails.data || !elementDetails.data._id) {
            return Promise.reject(new ErrorMessage('navigatin/PARAM_ERROR', 'Interne Annomalie'));
        }

        // request params setup
        var __params = new HttpParams();
        if (confirmed != undefined) {
            __params = __params.append('confirm', String(confirmed));
        }
        const __url = navigationConfig.urlDataPostDetails + '/' + elementDetails.data._id;
        if (appConfig.dev.mockingMode) {
            // log
            console.warn('Mocking HTTP request: ', __url);

            return Promise.resolve(null);
        }
        return this.httpPutAsJsonWithParams(
            __url,
            __params,
            elementDetails.data,
            navigationConfig.debugMode
        );
    }

    public setDefaults(nodeID: string): Promise<any> {
        if (!nodeID) {
            return Promise.reject(new ErrorMessage('navigatin/PARAM_ERROR', 'Interne Annomalie'));
        }

        const __params = new HttpParams();
        const __url = navigationConfig.urlSetDefaults + '/' +
            nodeID + '/setDefaults';

        return this.httpPostAsJsonWithParams(
            __url,
            __params,
            {},
            navigationConfig.debugMode
        );
    }

    public getChecker(): Promise<any> {
        const __params = new HttpParams();
        const __url = navigationConfig.urlCheckers;

        return this.httpGetAsJsonWithParams(
            __url,
            __params
        );
    }

    public getReport(id: string): Promise<any> {
        const __url = navigationConfig.urlCheckers + '/' + id;

        return this.httpGetAsBlob(
            __url,
            {
                observe: "response",
                responseType: "blob",
            },
        );  
    }

    public validateDocument(
        toValidate: {
            collection: string,
            document: any,
        }
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:validateDocument(called)'); }
        // sanity
        if (!toValidate || !toValidate.document) {
            return Promise.reject(new ErrorMessage('navigatin/PARAM_ERROR', 'Interne Annomalie'));
        }
        // request params setup
        const __params = new HttpParams();
        const __url = navigationConfig.urlValidation + '/' + toValidate.collection + '/checkValidation';
        // -- mocking case ---------------------------------------------------
        if (appConfig.dev.mockingMode) {
            // log
            console.warn('Mocking HTTP request: ', __url);
            // create result document
            return Promise.resolve({
                error: undefined,
            });
        }

        // -- HTTP case ------------------------------------------------------
        return this.httpPostAsJsonWithParams(
            __url,
            __params,
            toValidate.document,
            navigationConfig.debugMode
        );
    }

    public newEdgeAndElement(
        newEdgeAndElement: INavNewEdgeAndElement
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:newEdgeAndElement(called)'); }
        // sanity
        if (!newEdgeAndElement || !newEdgeAndElement.data) {
            return Promise.reject(new ErrorMessage('navigatin/PARAM_ERROR', 'Interne Annomalie'));
        }
        // request params setup
        let __params = new HttpParams();
        const __url = navigationConfig.urlNewEdgeAndElement + '/' +
            newEdgeAndElement.fromID + '/newRelatedDocument';
        // -- mocking case ---------------------------------------------------
        if (appConfig.dev.mockingMode) {
            // log
            console.warn('Mocking HTTP request: ', __url);

            const rand = Math.floor(Math.random() * 10000);
            // direction
            let from = newEdgeAndElement.fromID;
            let to = newEdgeAndElement.elemType + '/' + rand.toString();
            if (newEdgeAndElement.direction === 'from') {
                to = newEdgeAndElement.fromID;
                from = newEdgeAndElement.elemType + '/' + rand.toString();
            }
            // create result document
            return Promise.resolve({
                edge: {
                    _id: newEdgeAndElement.edgeType + '/' + rand.toString(),
                    _from: from,
                    _to: to,
                    aktion: undefined,
                },
                document: {
                    _id: newEdgeAndElement.elemType + '/' + rand.toString(),
                    bezeichnung: rand.toString(),
                    anmerkung: newEdgeAndElement.elemType + '/' + rand.toString(),
                    typ: undefined,
                    art: undefined
                }
            });
        }

        if ('createDefaults' in newEdgeAndElement) {
            __params = __params.append('createDefaults', String(newEdgeAndElement.createDefaults));
        }

        if ('createComponentDefaults' in newEdgeAndElement) {
            __params = __params.append('createComponentDefaults', String(newEdgeAndElement.createComponentDefaults));
        }

        if ('createFGConnection' in newEdgeAndElement) {
            __params = __params.append('createFGConnection', String(newEdgeAndElement.createFGConnection));
        }

        // -- HTTP case ------------------------------------------------------
        return this.httpPostAsJsonWithParams(
            __url,
            __params,
            {
                edgeCollection: newEdgeAndElement.edgeType,
                documentCollection: newEdgeAndElement.elemType,
                direction: newEdgeAndElement.direction === 'to' ? 'fwd' : 'bkwd',
                document: newEdgeAndElement.data.data,           // data in Interface
            },
            navigationConfig.debugMode
        );
    }

    public loadElementsExtention(
        selectedNodeID: string,
        requestOption: INavRequestOption
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:loadElementsExtention(called)'); }

        // request params setup
        const __params = new HttpParams()
            .set('loadOptions', requestOption.loadOptions)
            .set('maxnodes', requestOption.maxnodes.toString());
        // edges: map array to single string
        let edgeParam = '';
        let index = 0;
        requestOption.edges.forEach(elem => {
            if (elem.search) {
                edgeParam = (index++ === 0) ? elem.value : edgeParam + ',' + elem.value;
            }
        });
        __params.set('edges', edgeParam);
        let __url = navigationConfig.urlDataExtendedSearch + '/' + selectedNodeID;

        // mocking case
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            __url = `${appConfig.dev.mockingDataFolder}/nav-search-respond-extended.json`;
            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpGetAsJsonWithParams(
            __url,
            __params,
            navigationConfig.debugMode
        );
    }

    // ---------------------------------------------------------------------------------------------
    // --- Lösche elements
    // ---------------------------------------------------------------------------------------------

    /**
    * Sendet request zum Löschen einer Edge zum Backend.
    *
    * @param edgeID: string
    * @returns Promise<any>
    */
    public removeEdge(
        edgeId: string
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:removeEdge(called)'); }

        // request params setup
        const __url = navigationConfig.urlDataRemoveEdge + '/' + edgeId;

        // -- mocking case ---------------------------------------------------
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            return Promise.resolve(null);
        }

        // -- http case ------------------------------------------------------
        return this.httpDelete(
            __url,
            navigationConfig.debugMode
        );
    }

    public deleteElements(elementIDs: string[]): Promise<any> {
        // debug output
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:deleteElements(called)'); }

        // request params setup
        const __url = navigationConfig.urlDataPatch;

        const __params = new HttpParams();
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            return Promise.resolve(null);
        }

        // regular HTTP case
        return this.httpPatchAsJsonWithParams(
            __url,
            __params,
            { delete: elementIDs },
            navigationConfig.debugMode
        );
    }

    // ---------------------------------------------------------------------------------------------
    // --- Erzeuge Elemente
    // ---------------------------------------------------------------------------------------------

    /**
    * Sendet POST mit Daten zur neuen Edge.
    *
    * @param edgeID: string
    * @returns Promise<any>
    */
    public addEdge(
        edge: INavElementsEdge
    ): Promise<any> {
        if (navigationConfig.debugMode) { console.log('[NavigationDatahubService]:addEdge(called)'); }

        // request params setup
        let __params = new HttpParams();
        let __url = navigationConfig.urlDataAddEdge + '/' + edge.group;

        const __edge: any = {};
        if (edge.aktion) {
            __edge._from = edge.from;
            __edge._to = edge.to;
            __edge.aktion = parseInt(edge.aktion, 10);
        } else {
            __edge._from = edge.from;
            __edge._to = edge.to;
        }


        if ('createDefaults' in edge) {
            __params = __params.append('createDefaults', String(edge.createDefaults));
        }

        if ('createComponentDefaults' in edge) {
            __params = __params.append('createComponentDefaults', String(edge.createComponentDefaults));
        }

        if ('createFGConnection' in edge) {
            __params = __params.append('createFGConnection', String(edge.createFGConnection));
        }

        // mocking case
        if (appConfig.dev.mockingMode) {

            // log
            console.warn('Mocking HTTP request: ', __url);

            __url = `${appConfig.dev.mockingDataFolder}/nav-add-edge.json`;
            return this.readFileJsonSchema(
                __url,
                navigationConfig.debugMode
            );
        }

        // regular HTTP case
        return this.httpPostAsJsonWithParams(
            __url,
            __params,
            __edge,
            navigationConfig.debugMode
        );
    }
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/service/navigation-datahub.service.ts